import { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Filters,
  KanbanStageLabel,
  KanbanTag,
  NotProvided,
  PersonMark,
  Page,
  PageHeader,
  Pagination,
  Table,
  Toolbar,
  type FilterFieldSlot,
} from '@evogard/ui';
import type { ClaimSummary } from '@evogard/contract';
import {
  CAF_STATUSES,
  analysisHoldLabel,
  count,
  formatInstant,
  type ClaimType,
} from '@evogard/domain';
import { can, useSessionContext } from '../../lib/session.js';
import { useDebouncedTerm } from '../../lib/debounce.js';
import { DataCell } from './DataCell.js';
import { HoldToggle } from './HoldToggle.js';
import { ClaimDeskModal } from './claim/ClaimDeskModal.js';
import { ClaimsBoard, stageColorOf } from './claim/ClaimsBoard.js';
import { showUndoToast, type LastMove } from './claim/UndoMove.js';
import { onClaimChanged } from './claim/useDesk.js';
import { CLAIM_TYPE_OPTIONS, labelOf, phaseLabel } from './claim/labels.js';
import {
  ANALYSIS_DESK,
  COMPENSATION_DESK,
  REPAIR_DESK,
  type Desk,
} from './claim/desk.js';
import { useClaimList, type ClaimDeadlineFilter } from './claim/useClaims.js';
import { dueLabel, slaLabel } from './claim/ClaimDeadlines.js';
import { ClaimLabelTags } from './claim/ClaimLabels.js';
import { useLabelOptions } from './claim/useLabels.js';

type View = 'quadro' | 'tabela';

/** Sinistros › Análise. */
export function AnalysisBoardScreen() {
  return <DepartmentBoardScreen desk={ANALYSIS_DESK} />;
}

/**
 * Sinistros › Reparo (07/10/2026): a mesma tela da Análise, sobre o catálogo
 * do Reparo. A Aprovação Supervisor aparece só para quem supervisiona, com os
 * mesmos cards da fila de Supervisão › Supervisão do Reparo (`supervision/`):
 * saiu do quadro em 29/10/2026 e voltou em 03/11/2026.
 */
export function RepairBoardScreen() {
  return <DepartmentBoardScreen desk={REPAIR_DESK} />;
}

/**
 * Sinistros › Indenização (09/10/2026): a mesma tela, sobre o catálogo do
 * de-para da Indenização. No lugar do quadro de exemplo que lia o pipe do
 * Pipefy (`compensation.json`), que continua no repositório como régua do
 * catálogo.
 */
export function CompensationBoardScreen() {
  return <DepartmentBoardScreen desk={COMPENSATION_DESK} />;
}

/**
 * Os sinistros que estão num departamento — a Análise ou o Reparo —, em
 * quadro ou em tabela.
 *
 * A fase é a do SINISTRO (`claims.current_phase` com `current_department`):
 * o departamento não tem fase própria. Por isso o quadro é o mesmo
 * recorte da lista de Sinistros, preso no departamento. As duas visões
 * dividem a busca e os filtros — trocar de visão não perde o recorte.
 *
 * ## O clique abre a mesa
 *
 * Os sinistros são reais, e clicar num deles abre o `ClaimDeskModal`: os
 * envolvidos, o formulário da fase, os destinos que o pipe permite, a
 * Atividade e os Anexos. Mover grava desde 05/10/2026, e a mesa fica no
 * sinistro que se moveu (decisão do dono em 06/10/2026).
 *
 * **Não há "Abrir sinistro" aqui** (decisão do dono em 06/10/2026): todo
 * sinistro chega à Análise pelo aviso, aberto na lista de Sinistros
 * (`claim/ClaimsScreen.tsx`). O botão existiu só para testar o formulário.
 */
function DepartmentBoardScreen({ desk }: { desk: Desk }) {
  const { data: context } = useSessionContext();
  /* As etapas do supervisor voltaram ao quadro em 03/11/2026 (decisão do
     dono), com os mesmos cards da fila de Supervisão › Supervisão do Reparo
     — e só para quem supervisiona: a quem não supervisiona, a coluna nem
     aparece, e a API já esconde os cards (29/10/2026). */
  const supervises =
    !desk.supervision || can(context, desk.supervision.resource, desk.supervision.action);
  const stages = supervises
    ? desk.workStages
    : desk.workStages.filter((code) => !desk.supervision?.phases.includes(code));
  const supervisionStages = supervises ? undefined : ('fora' as const);
  const [view, setView] = useState<View>('quadro');
  const [opened, setOpened] = useState<ClaimSummary | null>(null);
  /** Sobe a cada movimento, e o quadro recarrega as colunas. */
  const [version, setVersion] = useState(0);
  const [lastMove, setLastMove] = useState<LastMove | null>(null);
  /* Todo ato da mesa que muda onde o sinistro está recarrega o quadro —
     mover, desfazer (na mesa ou no canto), parar, retomar, registrar contato,
     salvar e anexar. Antes só mover recarregava. */
  useEffect(() => onClaimChanged(() => setVersion((v) => v + 1)), []);

  const [search, setSearch] = useState('');
  const term = useDebouncedTerm(search);
  const [phase, setPhase] = useState('');
  const [type, setType] = useState<ClaimType | ''>('');
  const [includeArchived, setIncludeArchived] = useState(false);
  /* O botão Parados: os que não estão parados, ou só os parados — nunca os dois. */
  const [onHold, setOnHold] = useState(false);
  const [situation, setSituation] = useState('');
  const [cafStatus, setCafStatus] = useState('');
  const [labelId, setLabelId] = useState('');
  const [deadline, setDeadline] = useState<ClaimDeadlineFilter | ''>('');
  /* O filtro oferece as etiquetas dos setores de quem usa — as que o card mostra. */
  const labelSectors = useLabelOptions();
  const labelOptions = (labelSectors.data ?? []).flatMap((s) =>
    s.labels.map((l) => ({
      value: String(l.id),
      label: l.name,
      group: s.departmentName,
    })),
  );
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const query = useClaimList({
    search: term,
    currentDepartment: desk.department,
    currentPhase: phase || undefined,
    supervisionStages,
    type: type || undefined,
    includeArchived,
    onHold,
    situation: situation || undefined,
    cafStatus: cafStatus || undefined,
    labelId: labelId ? Number(labelId) : undefined,
    deadline: deadline || undefined,
    page,
    perPage,
  });
  const result = query.data;
  const hasFilter =
    term.trim() !== '' ||
    phase !== '' ||
    type !== '' ||
    includeArchived ||
    onHold ||
    situation !== '' ||
    cafStatus !== '' ||
    labelId !== '' ||
    deadline !== '';

  const fields: FilterFieldSlot[] = [
    {
      type: 'selecao',
      name: 'phase',
      label: 'Fase',
      placeholder: 'Todas',
      /* Só as etapas de trabalho (07/10/2026): Aprovado para Reparo e
         Indenização levam o sinistro a outro departamento, e Desistência, os
         Negados e Concluído Somente Terceiro são fases gerais — nenhum deles é
         lugar da Análise. */
      options: stages.map((code) => ({
        value: code,
        label: phaseLabel(desk.department, code),
      })),
    },
    {
      type: 'selecao',
      name: 'situation',
      label: 'Situação',
      placeholder: 'Todas',
      /* As situações de cada etapa, sob o nome dela. */
      options: stages.flatMap((stage) =>
        (desk.phaseOf(stage)?.situations ?? []).map((code) => ({
          value: code,
          label: desk.situationLabel(code),
          group: desk.phaseOf(stage)!.label,
        })),
      ),
    },
    {
      type: 'selecao',
      name: 'deadline',
      label: 'Prazo',
      placeholder: 'Todos',
      /* O SLA EXPIRA e o card VENCE — cada relógio com a sua palavra. */
      options: [
        { value: 'sla_expirado', label: 'SLA da fase expirado', group: 'SLA da fase' },
        {
          value: 'sla_expira_em_breve',
          label: 'SLA expira em breve',
          group: 'SLA da fase',
        },
        { value: 'vencido', label: 'Vencidos', group: 'Vencimento' },
        {
          value: 'vence_em_breve',
          label: 'Vencem nas próximas 24h',
          group: 'Vencimento',
        },
        { value: 'sem_vencimento', label: 'Sem vencimento', group: 'Vencimento' },
      ],
    },
    {
      type: 'selecao',
      name: 'label',
      label: 'Etiqueta',
      placeholder: 'Todas',
      options: labelOptions,
    },
    {
      type: 'selecao',
      name: 'type',
      label: 'Tipo',
      placeholder: 'Todos',
      options: [
        { value: 'associado', label: 'Do associado' },
        { value: 'terceiro', label: 'Do terceiro' },
      ],
    },
    /* O resultado da CAF (16/10/2026) — só a Análise manda documentos à CAF. */
    ...(desk.department === 'analise'
      ? [
          {
            type: 'selecao' as const,
            name: 'caf',
            label: 'Resultado da CAF',
            placeholder: 'Todos',
            options: Object.entries(CAF_STATUSES).map(([value, label]) => ({
              value,
              label,
            })),
          },
        ]
      : []),
    {
      type: 'selecao',
      name: 'archived',
      label: 'Arquivados',
      placeholder: 'Esconder',
      options: [{ value: 'sim', label: 'Mostrar também os arquivados' }],
    },
  ];

  const searchSlot = {
    /* "da Análise", "do Reparo", "da Indenização" — o artigo vem da mesa. */
    label: `Pesquisar sinistros d${desk.the}`,
    placeholder: 'Qualquer dado do sinistro: nome, placa, relato, protocolo…',
    value: search,
    onChange: (value: string) => {
      setSearch(value);
      setPage(1);
    },
  };
  const filtersSlot = (
    <>
      <HoldToggle
        held={onHold}
        onChange={(held) => {
          setOnHold(held);
          setPage(1);
        }}
      />
      <Filters
        values={{
          phase,
          type,
          situation,
          caf: cafStatus,
          label: labelId,
          deadline,
          archived: includeArchived ? 'sim' : '',
        }}
        fields={fields}
        onApply={(values) => {
          setPhase(String(values.phase ?? ''));
          setType(String(values.type ?? '') as ClaimType | '');
          setIncludeArchived(values.archived === 'sim');
          setSituation(String(values.situation ?? ''));
          setCafStatus(String(values.caf ?? ''));
          setLabelId(String(values.label ?? ''));
          setDeadline(String(values.deadline ?? '') as ClaimDeadlineFilter | '');
          setPage(1);
        }}
      />
    </>
  );

  return (
    <Page measure="cheia">
      <PageHeader
        title={desk.name}
        actions={
          <Button
            variant="contorno"
            icon={view === 'quadro' ? 'documento' : 'quadro'}
            onClick={() => setView(view === 'quadro' ? 'tabela' : 'quadro')}
          >
            {view === 'quadro' ? 'Ver tabela' : 'Ver quadro'}
          </Button>
        }
      >
        Os sinistros de {context?.currentTenant.nome} que estão {desk.in}, por fase.
      </PageHeader>

      {view === 'quadro' ? (
        <ClaimsBoard
          department={desk.department}
          columns={stages}
          phase={phase || undefined}
          filter={{
            search: term,
            type: type || undefined,
            includeArchived,
            onHold,
            situation: situation || undefined,
            cafStatus: cafStatus || undefined,
            labelId: labelId ? Number(labelId) : undefined,
            deadline: deadline || undefined,
          }}
          toolbar={<Toolbar search={searchSlot} toolbar={filtersSlot} />}
          onOpen={setOpened}
          version={version}
        />
      ) : (
        <>
          {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
          {(!query.isError || result) && (
            <Card
              title={
                result === undefined
                  ? 'Carregando…'
                  : count(result.total, 'sinistro', 'sinistros')
              }
              descricao={includeArchived ? 'Ativos e arquivados.' : 'Só os ativos.'}
              bleed
            >
              <Table
                paginate={false}
                search={searchSlot}
                toolbar={filtersSlot}
                label={`Sinistros ${desk.in}`}
                items={result?.items ?? []}
                loading={query.isLoading}
                skeletonRows={perPage}
                rowKey={(c) => String(c.id)}
                rowTitle={(c) => `Sinistro #${c.id}`}
                rowSubtitle={(c) => positionOf(desk, c)}
                rowIcon="escudo"
                rowActions={(c) => [
                  { label: 'Abrir', icon: 'olho', run: () => setOpened(c) },
                ]}
                empty={
                  hasFilter
                    ? 'Nenhum sinistro da Análise com esses filtros.'
                    : 'Nenhum sinistro na Análise agora. Todo sinistro aberto em Sinistros nasce aqui, na Entrada.'
                }
                columns={[
                  {
                    title: 'Sinistro',
                    alignment: 'inicio' as const,
                    minWidth: '10rem',
                    cell: (c) => (
                      <DataCell icon={c.type === 'terceiro' ? 'terceiro' : 'pessoa'}>
                        <span className="font-semibold tabular-nums">#{c.id}</span>
                        <span className="truncate text-texto-suave">
                          {c.type === 'terceiro'
                            ? `Terceiro do #${c.parentClaimId}`
                            : 'Associado'}
                        </span>
                      </DataCell>
                    ),
                  },
                  {
                    title: 'Nome',
                    alignment: 'inicio' as const,
                    minWidth: '15rem',
                    cell: (c) => {
                      const name =
                        c.type === 'terceiro'
                          ? c.thirdParty?.name
                          : c.vehicle?.memberName;
                      return (
                        <div
                          className="flex min-w-0 items-center gap-2.5"
                          title={name ?? undefined}
                        >
                          <Avatar nome={name ?? null} size="md" />
                          {name ? (
                            <span className="truncate">{name}</span>
                          ) : (
                            <NotProvided />
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    title: desk.department === 'reparo' ? 'Responsável' : 'Analista',
                    alignment: 'inicio' as const,
                    minWidth: '12rem',
                    cell: (c) => (
                      <PersonMark
                        name={c.assignee?.name ?? null}
                        emptyLabel={desk.noAssignee}
                        className="text-sm"
                      />
                    ),
                  },
                  {
                    title: 'Etiquetas',
                    alignment: 'inicio' as const,
                    minWidth: '12rem',
                    /* Sem etiqueta, a célula fica vazia: não é dado faltando. */
                    cell: (c) => (
                      <div className="flex flex-wrap gap-1">
                        <ClaimLabelTags labels={c.labels} />
                      </div>
                    ),
                  },
                  {
                    title: 'Fase',
                    minWidth: '13rem',
                    /* O ponto da cor da etapa, a mesma do cabeçalho do quadro. */
                    cell: (c) => (
                      <KanbanStageLabel
                        stage={{
                          name: positionOf(desk, c),
                          color: stageColorOf(desk.department, c.currentPhase),
                        }}
                      />
                    ),
                  },
                  /* Os dois relógios (07/10/2026): o SLA da fase e o vencimento. */
                  {
                    title: 'Na fase',
                    minWidth: '10rem',
                    cell: (c) => {
                      const sla =
                        c.phaseEnteredAt && c.phaseDeadlineAt
                          ? slaLabel(c.phaseEnteredAt, c.phaseDeadlineAt)
                          : null;
                      return sla ? (
                        <KanbanTag icon="relogio" tone={sla.tone}>
                          {sla.text}
                        </KanbanTag>
                      ) : null;
                    },
                  },
                  {
                    title: 'Vencimento',
                    minWidth: '10rem',
                    cell: (c) => {
                      const due = c.dueAt ? dueLabel(c.dueAt) : null;
                      return due ? (
                        <KanbanTag icon="calendario" tone={due.tone}>
                          {due.text}
                        </KanbanTag>
                      ) : null;
                    },
                  },
                  {
                    title: 'Tipo',
                    minWidth: '11rem',
                    cell: (c) => (
                      <DataCell icon="etiqueta">
                        {labelOf(CLAIM_TYPE_OPTIONS, c.claimType)}
                      </DataCell>
                    ),
                  },
                  {
                    title: 'Ocorrido',
                    minWidth: '11rem',
                    cell: (c) => (
                      <DataCell icon="calendario">
                        <span className="tabular-nums">
                          {formatInstant(c.occurredAt)}
                        </span>
                      </DataCell>
                    ),
                  },
                ]}
              />
              {result && (
                <Pagination
                  page={result.page}
                  perPage={result.perPage}
                  total={result.total}
                  onChange={setPage}
                  onPerPageChange={(n) => {
                    setPerPage(n);
                    setPage(1);
                  }}
                  label="sinistros"
                  className="border-t border-borda-suave px-4 py-3"
                />
              )}
            </Card>
          )}
        </>
      )}

      <ClaimDeskModal
        /* A chave troca o estado do modal (destino escolhido, aba) junto com o aviso. */
        key={opened?.id ?? 'fechado'}
        claim={opened}
        onClose={() => {
          /* Fechar a mesa com o Desfazer ainda valendo leva o aviso para o canto
             da tela — fechar não pode custar o Desfazer. */
          if (lastMove) showUndoToast(lastMove);
          setLastMove(null);
          setOpened(null);
        }}
        onOpenClaim={setOpened}
        onMoved={(move) => {
          /* Moveu, a mesa FECHA (decisão do dono em 15/10/2026, no lugar da de
             06/10, em que ela ficava no sinistro): o card que vai para outro
             departamento não fica aberto numa mesa que não é mais a dele. O
             Desfazer vai para o canto da tela; o quadro recarrega pelo aviso
             de `onClaimChanged`. */
          showUndoToast(move);
          setLastMove(null);
          setOpened(null);
        }}
        lastMove={lastMove}
        onLastMoveDone={() => setLastMove(null)}
      />
    </Page>
  );
}

/**
 * Onde o sinistro está: a etapa, e a situação ou a parada dentro dela —
 * "Entrada · Aguardando retorno", "Documentação · parado: Pendência".
 */
function positionOf(desk: Desk, c: ClaimSummary): string {
  return [
    phaseLabel(desk.department, c.currentPhase),
    c.hold ? `parado: ${analysisHoldLabel(c.hold)}` : null,
    c.situation ? desk.situationLabel(c.situation) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}
